/**
 * ErrorQuestionCard.js - VERSÃO PREMIUM
 * 
 * Card ultra-visual para questões erradas
 * Design glassmorphism com gradientes, blur e animações ricas
 */

import React, { useRef, useEffect, useState } from 'react';
import {
    View,
    StyleSheet,
    TouchableOpacity,
    Animated,
    Dimensions,
    Easing, Platform,
} from 'react-native';
import { Text } from '../ui/AppText';
import { LinearGradient } from 'expo-linear-gradient';
import { BlurView } from 'expo-blur';
import { SHORT_TERM_COLORS, CATEGORIES, SHORT_TERM_MEMORY_CONFIG } from '../../constants/categories';
import ErrorCountBadge from './ErrorCountBadge';
import RedemptionMeter from './RedemptionMeter';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

const ErrorQuestionCard = ({
    question,
    onPress,
    onPractice,
    onView,
    index = 0,
    isDarkMode = true,
    expanded = false,
}) => {
    const [isExpanded, setIsExpanded] = useState(expanded);

    // Animações
    const scaleAnim = useRef(new Animated.Value(0.9)).current;
    const opacityAnim = useRef(new Animated.Value(0)).current;
    const slideAnim = useRef(new Animated.Value(50)).current;
    const expandAnim = useRef(new Animated.Value(0)).current;
    const glowAnim = useRef(new Animated.Value(0.5)).current;
    const shineAnim = useRef(new Animated.Value(-200)).current;

    // Animação de entrada
    useEffect(() => {
        const delay = index * 100;

        Animated.parallel([
            Animated.spring(scaleAnim, {
                toValue: 1,
                tension: 60,
                friction: 8,
                delay,
                useNativeDriver: true,
            }),
            Animated.timing(opacityAnim, {
                toValue: 1,
                duration: 400,
                delay,
                useNativeDriver: true,
            }),
            Animated.timing(slideAnim, {
                toValue: 0,
                duration: 400,
                delay,
                easing: Easing.out(Easing.back(1.5)),
                useNativeDriver: true,
            }),
        ]).start();
    }, [index, scaleAnim, opacityAnim, slideAnim]);

    // Animação de glow contínuo
    useEffect(() => {
        const glow = Animated.loop(
            Animated.sequence([
                Animated.timing(glowAnim, { toValue: 1, duration: 2000, useNativeDriver: Platform.OS !== 'web' }),
                Animated.timing(glowAnim, { toValue: 0.3, duration: 2000, useNativeDriver: Platform.OS !== 'web' }),
            ])
        );
        glow.start();
        return () => glow.stop();
    }, [glowAnim]);

    // Shine animation (sweep)
    useEffect(() => {
        const shine = Animated.loop(
            Animated.sequence([
                Animated.timing(shineAnim, {
                    toValue: SCREEN_WIDTH + 200,
                    duration: 3000,
                    useNativeDriver: Platform.OS !== 'web',
                }),
                Animated.timing(shineAnim, {
                    toValue: -200,
                    duration: 0,
                    useNativeDriver: Platform.OS !== 'web',
                }),
                Animated.delay(2000),
            ])
        );
        shine.start();
        return () => shine.stop();
    }, [shineAnim]);

    // Toggle expand
    const handlePress = () => {
        setIsExpanded(!isExpanded);
        Animated.spring(expandAnim, {
            toValue: isExpanded ? 0 : 1,
            tension: 100,
            friction: 10,
            useNativeDriver: false,
        }).start();
        onPress?.();
    };

    // Cores baseadas na categoria
    const categoryInfo = CATEGORIES[question.category] || CATEGORIES.GERAL;

    // Formatar data
    const formatDate = (timestamp) => {
        if (!timestamp) return 'Nunca';
        const date = new Date(timestamp);
        const now = new Date();
        const diff = now - date;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);

        if (hours < 1) return 'Agora mesmo';
        if (hours < 24) return `${hours}h atrás`;
        if (days === 1) return 'Ontem';
        if (days < 7) return `${days} dias atrás`;
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
    };

    // Determinar urgência
    const getUrgencyLevel = () => {
        if (question.errorCount >= 10) return 'critical';
        if (question.errorCount >= 5) return 'high';
        if (question.errorCount >= 3) return 'medium';
        return 'low';
    };

    const urgency = getUrgencyLevel();

    // Cores por urgência
    const getUrgencyColors = () => {
        switch (urgency) {
            case 'critical':
                return {
                    border: '#FF0844',
                    gradient: ['rgba(255, 8, 68, 0.15)', 'rgba(255, 8, 68, 0.05)'],
                    glow: '#FF0844',
                    label: 'CRÍTICO',
                    labelBg: '#FF0844',
                };
            case 'high':
                return {
                    border: '#FF6B6B',
                    gradient: ['rgba(255, 107, 107, 0.12)', 'rgba(255, 107, 107, 0.04)'],
                    glow: '#FF6B6B',
                    label: 'ALTA PRIORIDADE',
                    labelBg: '#FF6B6B',
                };
            case 'medium':
                return {
                    border: '#FF9F43',
                    gradient: ['rgba(255, 159, 67, 0.10)', 'rgba(255, 159, 67, 0.03)'],
                    glow: '#FF9F43',
                    label: null,
                    labelBg: null,
                };
            default:
                return {
                    border: 'rgba(255,255,255,0.1)',
                    gradient: ['rgba(255,255,255,0.05)', 'rgba(255,255,255,0.02)'],
                    glow: 'transparent',
                    label: null,
                    labelBg: null,
                };
        }
    };

    const urgencyColors = getUrgencyColors();

    // Altura expandida
    const cardHeight = expandAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [180, 380],
    });

    return (
        <Animated.View
            style={[
                styles.wrapper,
                {
                    opacity: opacityAnim,
                    transform: [
                        { scale: scaleAnim },
                        { translateX: slideAnim },
                    ],
                },
            ]}
        >
            <TouchableOpacity
                activeOpacity={0.9}
                onPress={handlePress}
            >
                <Animated.View style={[styles.cardContainer, { minHeight: cardHeight }]}>
                    {/* Background com gradiente */}
                    <LinearGradient
                        colors={isDarkMode ? urgencyColors.gradient : ['#FFFFFF', '#F8F9FA']}
                        style={styles.background}
                    />

                    {/* Borda lateral colorida */}
                    <Animated.View
                        style={[
                            styles.sideBorder,
                            {
                                backgroundColor: urgencyColors.border,
                                opacity: glowAnim,
                                shadowColor: urgencyColors.glow,
                            }
                        ]}
                    />

                    {/* Shine effect */}
                    <Animated.View
                        style={[
                            styles.shineEffect,
                            { transform: [{ translateX: shineAnim }] }
                        ]}
                    />

                    {/* Conteúdo principal */}
                    <View style={styles.content}>
                        {/* Header com categoria e badge */}
                        <View style={styles.header}>
                            {/* Categoria */}
                            <View style={styles.categorySection}>
                                <View style={[styles.categoryIcon, { backgroundColor: categoryInfo.color + '20' }]}>
                                    <Text style={styles.categoryEmoji}>{categoryInfo.icon}</Text>
                                </View>
                                <View style={styles.categoryInfo}>
                                    <Text style={styles.categoryName}>{categoryInfo.label}</Text>
                                    <View style={styles.metaRow}>
                                        <Text style={styles.metaText}>⏰ {formatDate(question.lastErrorAt)}</Text>
                                        <Text style={styles.metaDot}>•</Text>
                                        <Text style={styles.metaText}>🔄 {question.totalAttempts || 0} tentativas</Text>
                                    </View>
                                </View>
                            </View>

                            {/* Badge de erros */}
                            <View style={styles.badgeContainer}>
                                <ErrorCountBadge
                                    count={question.errorCount}
                                    size="large"
                                    showPulse={urgency === 'critical' || urgency === 'high'}
                                    showGlow={true}
                                    showFlame={urgency === 'critical'}
                                />
                            </View>
                        </View>

                        {/* Label de urgência */}
                        {urgencyColors.label && (
                            <View style={[styles.urgencyLabel, { backgroundColor: urgencyColors.labelBg }]}>
                                <Text style={styles.urgencyLabelText}>{urgencyColors.label}</Text>
                            </View>
                        )}

                        {/* Texto da questão */}
                        <Text
                            style={styles.questionText}
                            numberOfLines={isExpanded ? 5 : 2}
                        >
                            {question.text}
                        </Text>

                        {/* Seção expandida */}
                        {isExpanded && (
                            <Animated.View style={styles.expandedSection}>
                                {/* Medidor de redenção */}
                                <RedemptionMeter
                                    consecutiveCorrect={question.consecutiveCorrect || 0}
                                    showLabel={true}
                                    showParticles={true}
                                />

                                {/* Estatísticas detalhadas */}
                                <View style={styles.statsGrid}>
                                    <View style={styles.statItem}>
                                        <Text style={styles.statValue}>{question.errorCount}</Text>
                                        <Text style={styles.statLabel}>Total Erros</Text>
                                    </View>
                                    <View style={styles.statItem}>
                                        <Text style={styles.statValue}>{question.consecutiveCorrect || 0}</Text>
                                        <Text style={styles.statLabel}>Acertos Consec.</Text>
                                    </View>
                                    <View style={styles.statItem}>
                                        <Text style={styles.statValue}>
                                            {SHORT_TERM_MEMORY_CONFIG.CONSECUTIVE_CORRECT_TO_PROMOTE - (question.consecutiveCorrect || 0)}
                                        </Text>
                                        <Text style={styles.statLabel}>Faltam</Text>
                                    </View>
                                </View>
                            </Animated.View>
                        )}

                        {/* Botões de ação */}
                        <View style={styles.actionsRow}>
                            <TouchableOpacity
                                style={styles.actionButton}
                                onPress={onView}
                            >
                                <LinearGradient
                                    colors={['rgba(255,255,255,0.1)', 'rgba(255,255,255,0.05)']}
                                    style={styles.actionButtonGradient}
                                >
                                    <Text style={styles.actionIcon}>📖</Text>
                                    <Text style={styles.actionText}>Revisar</Text>
                                </LinearGradient>
                            </TouchableOpacity>

                            <TouchableOpacity
                                style={[styles.actionButton, styles.primaryButton]}
                                onPress={onPractice}
                            >
                                <LinearGradient
                                    colors={[SHORT_TERM_COLORS.primary, SHORT_TERM_COLORS.primaryDark]}
                                    style={styles.actionButtonGradient}
                                    start={{ x: 0, y: 0 }}
                                    end={{ x: 1, y: 1 }}
                                >
                                    <Text style={styles.actionIcon}>⚡</Text>
                                    <Text style={styles.actionTextPrimary}>Praticar Agora</Text>
                                </LinearGradient>
                            </TouchableOpacity>
                        </View>

                        {/* Indicador de expansão */}
                        <View style={styles.expandIndicator}>
                            <Text style={styles.expandIcon}>{isExpanded ? '▲' : '▼'}</Text>
                        </View>
                    </View>
                </Animated.View>
            </TouchableOpacity>
        </Animated.View>
    );
};

const styles = StyleSheet.create({
    wrapper: {
        marginBottom: 16,
        marginHorizontal: 16,
    },
    cardContainer: {
        borderRadius: 24,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.08)',
    },
    background: {
        ...StyleSheet.absoluteFillObject,
    },
    sideBorder: {
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 5,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.8,
        shadowRadius: 10,
    },
    shineEffect: {
        position: 'absolute',
        top: 0,
        width: 100,
        height: '100%',
        backgroundColor: 'rgba(255,255,255,0.05)',
        transform: [{ skewX: '-20deg' }],
    },
    content: {
        padding: 16,
        paddingLeft: 20,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 12,
    },
    categorySection: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
    },
    categoryIcon: {
        width: 48,
        height: 48,
        borderRadius: 14,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 12,
    },
    categoryEmoji: {
        fontSize: 24,
    },
    categoryInfo: {
        flex: 1,
    },
    categoryName: {
        fontSize: 16,
        fontWeight: '700',
        color: '#E8EAED',
        marginBottom: 4,
    },
    metaRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    metaText: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.5)',
    },
    metaDot: {
        marginHorizontal: 6,
        color: 'rgba(255,255,255,0.3)',
    },
    badgeContainer: {
        marginLeft: 12,
    },
    urgencyLabel: {
        alignSelf: 'flex-start',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 6,
        marginBottom: 12,
    },
    urgencyLabelText: {
        color: '#FFFFFF',
        fontSize: 10,
        fontWeight: '800',
        letterSpacing: 0.5,
    },
    questionText: {
        fontSize: 15,
        fontWeight: '500',
        color: '#E8EAED',
        lineHeight: 22,
        marginBottom: 16,
    },
    expandedSection: {
        marginTop: 8,
    },
    statsGrid: {
        flexDirection: 'row',
        justifyContent: 'space-around',
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderRadius: 16,
        padding: 16,
        marginTop: 16,
    },
    statItem: {
        alignItems: 'center',
    },
    statValue: {
        fontSize: 24,
        fontWeight: '800',
        color: '#E8EAED',
    },
    statLabel: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.5)',
        marginTop: 4,
    },
    actionsRow: {
        flexDirection: 'row',
        gap: 12,
    },
    actionButton: {
        flex: 1,
        borderRadius: 14,
        overflow: 'hidden',
    },
    primaryButton: {
        flex: 1.5,
    },
    actionButtonGradient: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 14,
        paddingHorizontal: 16,
        gap: 8,
    },
    actionIcon: {
        fontSize: 16,
    },
    actionText: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 14,
        fontWeight: '600',
    },
    actionTextPrimary: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '700',
    },
    expandIndicator: {
        alignItems: 'center',
        marginTop: 12,
    },
    expandIcon: {
        color: 'rgba(255,255,255,0.3)',
        fontSize: 12,
    },
});

export default ErrorQuestionCard;
